import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, SparklesIcon, PackageIcon, ShieldIcon, ClockIcon, CheckIcon, ArrowRightIcon, StarIcon, LockIcon, VideoIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { useCart } from '../contexts/CartContext';
import { BASE_PRICES } from '../contexts/PackagesContext';
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast' | 'consultation';
interface ServicePurchaseSectionProps {
  serviceType: ServiceType;
  onNavigate: (page: string) => void;
}
const SERVICE_CONFIG: Record<ServiceType, {
  name: string;
  betaPrice: string;
  premiumPrice: string;
  betaFeatures: string[];
  premiumFeatures: string[];
  betaPages: string;
  premiumPages: string;
  color: string;
  demoPage: string;
}> = {
  natal: {
    name: 'Натальная карта',
    betaPrice: 'Бесплатно',
    premiumPrice: '3 900 ₽',
    betaFeatures: ['Анализ Солнца, Луны и Асцендента', 'Основные черты характера', 'Общие рекомендации'],
    premiumFeatures: ['Всё из Beta отчёта', 'Все планеты в знаках и домах', 'Аспекты между планетами', 'Карьера, финансы, отношения', 'Кармические задачи и узлы', 'Компенсаторика и рекомендации'],
    betaPages: '1-3 страницы',
    premiumPages: '30-40 страниц',
    color: 'indigo',
    demoPage: 'report-preview-natal'
  },
  solar: {
    name: 'Соляр',
    betaPrice: 'Бесплатно',
    premiumPrice: '4 400 ₽',
    betaFeatures: ['Общая тема и фокус года', 'Основные планетарные влияния', 'Краткий прогноз по сферам'],
    premiumFeatures: ['Всё из Beta отчёта', 'Все планеты в домах соляра', 'Аспекты солярной карты', 'Помесячный прогноз', 'Карьера, финансы, отношения', 'Зоны риска и рекомендации'],
    betaPages: '1-3 страницы',
    premiumPages: '25-35 страниц',
    color: 'amber',
    demoPage: 'report-preview-solar'
  },
  compatibility: {
    name: 'Совместимость',
    betaPrice: 'Бесплатно',
    premiumPrice: '4 900 ₽',
    betaFeatures: ['Общий анализ совместимости', 'Взаимодействие Солнца и Луны', 'Основные сильные стороны пары'],
    premiumFeatures: ['Всё из Beta отчёта', 'Взаимодействие Венеры и Марса', 'Все аспекты между картами', 'Эмоциональная и сексуальная совместимость', 'Кармические связи', 'Практические рекомендации для пары'],
    betaPages: '1-3 страницы',
    premiumPages: '30-40 страниц',
    color: 'rose',
    demoPage: 'report-preview-compatibility'
  },
  combo: {
    name: 'Комбо: Натал + Соляр',
    betaPrice: 'Бесплатно',
    premiumPrice: '6 900 ₽',
    betaFeatures: ['Краткий профиль личности', 'Краткий прогноз года', 'Основные рекомендации'],
    premiumFeatures: ['Полный анализ натальной карты', 'Полный соляр на год', 'Все планеты и аспекты', 'Карьера, финансы, отношения', 'Помесячный прогноз', 'Компенсаторика и рекомендации'],
    betaPages: '1-3 страницы',
    premiumPages: '50-60 страниц',
    color: 'violet',
    demoPage: 'report-preview-combo'
  },
  forecast: {
    name: 'Прогноз (Транзиты)',
    betaPrice: 'Бесплатно',
    premiumPrice: '3 900 ₽',
    betaFeatures: ['Основной транзит периода', 'Краткий прогноз', 'Общие рекомендации'],
    premiumFeatures: ['Всё из Beta отчёта', 'Полный анализ транзитов', 'Ключевые даты и периоды', 'Карьера, финансы, отношения', 'Зоны риска', 'Практические рекомендации'],
    betaPages: '1-3 страницы',
    premiumPages: '20-30 страниц',
    color: 'teal',
    demoPage: 'report-preview-forecast'
  },
  consultation: {
    name: 'Консультация',
    betaPrice: '',
    premiumPrice: 'от 2 990 ₽',
    betaFeatures: [],
    premiumFeatures: ['Живое общение с астрологом', 'Персональный разбор карты', 'Ответы на ваши вопросы', 'Видеозапись встречи', 'PDF-итоги консультации'],
    betaPages: '',
    premiumPages: '60-90 минут',
    color: 'sky',
    demoPage: 'astrologers'
  }
};
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function ServicePurchaseSection({
  serviceType,
  onNavigate
}: ServicePurchaseSectionProps) {
  const config = SERVICE_CONFIG[serviceType];
  const isConsultation = serviceType === 'consultation';
  const {
    addItem
  } = useCart();
  const handleBuyPremium = () => {
    const priceNum = parseInt(config.premiumPrice.replace(/[^\d]/g, ''));
    addItem({
      id: `premium-${serviceType}-${Date.now()}`,
      name: `Premium отчёт: ${config.name}`,
      description: 'Полный профессиональный отчёт с глубоким анализом и практическими рекомендациями.',
      price: priceNum,
      type: 'premium-report',
      serviceName: config.name,
      format: 'PDF-отчёт',
      duration: config.premiumPages,
      badges: [{
        label: 'Premium',
        variant: 'pro'
      }]
    });
    onNavigate('checkout');
  };
  const handleBuyPackage = () => {
    addItem({
      id: `pkg-20-${Date.now()}`,
      name: 'Пакет 20 отчётов',
      description: 'Купите пакет отчётов со скидкой и используйте для любых услуг платформы.',
      price: BASE_PRICES[20],
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }]
    });
    onNavigate('checkout');
  };
  const colorClasses: Record<string, {
    bg: string;
    text: string;
    ring: string;
    badge: string;
  }> = {
    indigo: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-600',
      ring: 'ring-indigo-100 border-indigo-200',
      badge: 'bg-indigo-600'
    },
    amber: {
      bg: 'bg-amber-50',
      text: 'text-amber-600',
      ring: 'ring-amber-100 border-amber-200',
      badge: 'bg-amber-500'
    },
    rose: {
      bg: 'bg-rose-50',
      text: 'text-rose-600',
      ring: 'ring-rose-100 border-rose-200',
      badge: 'bg-rose-500'
    },
    violet: {
      bg: 'bg-violet-50',
      text: 'text-violet-600',
      ring: 'ring-violet-100 border-violet-200',
      badge: 'bg-violet-600'
    },
    teal: {
      bg: 'bg-teal-50',
      text: 'text-teal-600',
      ring: 'ring-teal-100 border-teal-200',
      badge: 'bg-teal-600'
    },
    sky: {
      bg: 'bg-sky-50',
      text: 'text-sky-600',
      ring: 'ring-sky-100 border-sky-200',
      badge: 'bg-sky-600'
    }
  };
  const colors = colorClasses[config.color];
  if (isConsultation) {
    return <div className="py-14 sm:py-24 bg-white" data-id="element-18691">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18692">
          <div className="text-center mb-8 sm:mb-16" data-id="element-18693">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-18694">
              Запишитесь на консультацию
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-18695">
              Выберите астролога и формат консультации
            </p>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-18696">
            {/* Consultation Card */}
            <motion.div variants={staggerItem} data-id="element-18697">
              <Card padding="lg" className={`flex flex-col h-full ${colors.ring} ring-1 shadow-xl relative`} data-id="element-18698">
                <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 ${colors.badge} text-white text-xs font-bold px-4 py-1 rounded-full`} data-id="element-18699">
                  Рекомендуем
                </div>
                <div className="flex items-center gap-3 mb-4" data-id="element-18700">
                  <div className={`w-12 h-12 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center`} data-id="element-18701">
                    <VideoIcon className="w-6 h-6" data-id="element-18702" />
                  </div>
                  <div data-id="element-18703">
                    <Badge variant="pro" className="mb-1" data-id="element-18704">
                      Живое общение
                    </Badge>
                    <h3 className="text-xl font-bold text-slate-900" data-id="element-18705">
                      Консультация с астрологом
                    </h3>
                  </div>
                </div>
                <p className="text-slate-500 mb-6 flex-grow" data-id="element-18706">
                  Персональный разбор вашей карты с профессиональным астрологом.
                  Ответы на все ваши вопросы.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-18707">
                  {config.premiumFeatures.map((feature, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-18708">
                      <CheckIcon className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-18709" />
                      {feature}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-18710">
                  {config.premiumPrice}
                </div>
                <Button variant="primary" fullWidth onClick={() => onNavigate('astrologers')} className={`${colors.badge} hover:opacity-90`} data-id="element-18711">
                  Выбрать астролога
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-18712" />
                </Button>
              </Card>
            </motion.div>

            {/* Package Card */}
            <motion.div variants={staggerItem} data-id="element-18713">
              <Card padding="lg" className="flex flex-col h-full border-slate-200" data-id="element-18714">
                <div className="flex items-center gap-3 mb-4" data-id="element-18715">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-18716">
                    <PackageIcon className="w-6 h-6" data-id="element-18717" />
                  </div>
                  <div data-id="element-18718">
                    <Badge variant="success" className="mb-1" data-id="element-18719">
                      Выгода до 40%
                    </Badge>
                    <h3 className="text-xl font-bold text-slate-900" data-id="element-18720">
                      Пакеты отчётов
                    </h3>
                  </div>
                </div>
                <p className="text-slate-500 mb-6 flex-grow" data-id="element-18721">
                  Приобретите пакет отчётов со скидкой и используйте их для
                  любых услуг платформы.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-18722">
                  <li className="flex items-start text-sm text-slate-600" data-id="element-18723">
                    <CheckIcon className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-18724" />
                    Пакет 20 отчётов — 76 000 ₽ (3 800 ₽/шт)
                  </li>
                  <li className="flex items-start text-sm text-slate-600" data-id="element-18725">
                    <CheckIcon className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-18726" />
                    Пакет 50 отчётов — 180 000 ₽ (3 600 ₽/шт)
                  </li>
                  <li className="flex items-start text-sm text-slate-600" data-id="element-18727">
                    <CheckIcon className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-18728" />
                    Пакет 100 отчётов — 315 000 ₽ (3 150 ₽/шт)
                  </li>
                </ul>
                <div className="text-lg font-bold text-emerald-600 mb-6" data-id="element-18729">
                  Экономия до 40% от розничной цены
                </div>
                <Button variant="outline" fullWidth onClick={() => onNavigate('pricing')} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-id="element-18730">
                  Выбрать пакет
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-18731" />
                </Button>
              </Card>
            </motion.div>
          </motion.div>

          {/* Trust Badges */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500" data-id="element-18732">
            <span className="flex items-center" data-id="element-18733">
              <ShieldIcon className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-18734" />
              Безопасная оплата
            </span>
            <span className="flex items-center" data-id="element-18735">
              <ClockIcon className="w-4 h-4 mr-1.5 text-sky-500" data-id="element-18736" />
              Запись на удобное время
            </span>
            <span className="flex items-center" data-id="element-18737">
              <StarIcon className="w-4 h-4 mr-1.5 text-amber-500" data-id="element-18738" />
              Проверенные астрологи
            </span>
          </div>
        </div>
      </div>;
  }
  return <div className="py-14 sm:py-24 bg-white" data-id="element-18739">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18740">
        <div className="text-center mb-8 sm:mb-16" data-id="element-18741">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-18742">
            Выберите формат отчёта
          </h2>
          <p className="text-base sm:text-lg text-slate-600" data-id="element-18743">
            Бесплатный обзор, полный разбор или выгодный пакет
          </p>
        </div>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-18744">
          {/* Beta Card */}
          <motion.div variants={staggerItem} data-id="element-18745">
            <Card padding="lg" className="flex flex-col h-full border-slate-200" data-id="element-18746">
              <div className="flex items-center gap-3 mb-4" data-id="element-18747">
                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center" data-id="element-18748">
                  <EyeIcon className="w-5 h-5" data-id="element-18749" />
                </div>
                <div data-id="element-18750">
                  <Badge variant="neutral" className="mb-1" data-id="element-18751">
                    Базовый
                  </Badge>
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-18752">
                    Beta отчёт
                  </h3>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-4 flex-grow" data-id="element-18753">
                Краткий анализ — основные показатели и общие рекомендации для
                знакомства с услугой.
              </p>
              <ul className="space-y-2.5 mb-6" data-id="element-18754">
                {config.betaFeatures.map((feature, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-18755">
                    <CheckIcon className="w-4 h-4 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-18756" />
                    {feature}
                  </li>)}
              </ul>
              <div className="pt-4 border-t border-slate-100 mt-auto" data-id="element-18757">
                <div className="flex items-baseline justify-between mb-4" data-id="element-18758">
                  <span className="text-2xl font-bold text-emerald-600" data-id="element-18759">
                    {config.betaPrice}
                  </span>
                  <span className="text-xs text-slate-400" data-id="element-18760">
                    {config.betaPages}
                  </span>
                </div>
                <Button variant="outline" fullWidth onClick={() => onNavigate(config.demoPage)} data-id="element-18761">
                  Посмотреть пример Beta
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Premium Card - Highlighted */}
          <motion.div variants={staggerItem} data-id="element-18762">
            <Card padding="lg" className={`flex flex-col h-full ${colors.ring} ring-1 shadow-xl relative`} data-id="element-18763">
              <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 ${colors.badge} text-white text-xs font-bold px-4 py-1 rounded-full`} data-id="element-18764">
                Рекомендуем
              </div>
              <div className="flex items-center gap-3 mb-4" data-id="element-18765">
                <div className={`w-10 h-10 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center`} data-id="element-18766">
                  <SparklesIcon className="w-5 h-5" data-id="element-18767" />
                </div>
                <div data-id="element-18768">
                  <Badge variant="pro" className="mb-1" data-id="element-18769">
                    Полный
                  </Badge>
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-18770">
                    Premium отчёт
                  </h3>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-4 flex-grow" data-id="element-18771">
                Полный профессиональный отчёт с глубоким анализом и
                практическими рекомендациями.
              </p>
              <ul className="space-y-2.5 mb-6" data-id="element-18772">
                {config.premiumFeatures.map((feature, i) => <li key={i} className="flex items-start text-sm text-slate-700 font-medium" data-id="element-18773">
                    <CheckIcon className={`w-4 h-4 ${colors.text} mr-2 mt-0.5 flex-shrink-0`} data-id="element-18774" />
                    {feature}
                  </li>)}
              </ul>
              <div className="pt-4 border-t border-slate-100 mt-auto" data-id="element-18775">
                <div className="flex items-baseline justify-between mb-4" data-id="element-18776">
                  <span className="text-2xl font-bold text-slate-900" data-id="element-18777">
                    {config.premiumPrice}
                  </span>
                  <span className="text-xs text-slate-400" data-id="element-18778">
                    {config.premiumPages}
                  </span>
                </div>
                <Button variant="primary" fullWidth onClick={handleBuyPremium} className={`${colors.badge} hover:opacity-90`} data-id="element-18779">
                  Купить Premium отчёт
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-18780" />
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Package Card */}
          <motion.div variants={staggerItem} data-id="element-18781">
            <Card padding="lg" className="flex flex-col h-full border-emerald-200 bg-gradient-to-b from-emerald-50/30 to-white" data-id="element-18782">
              <div className="flex items-center gap-3 mb-4" data-id="element-18783">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-18784">
                  <PackageIcon className="w-5 h-5" data-id="element-18785" />
                </div>
                <div data-id="element-18786">
                  <Badge variant="success" className="mb-1" data-id="element-18787">
                    Выгода до 30%
                  </Badge>
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-18788">
                    Пакеты отчётов
                  </h3>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-4 flex-grow" data-id="element-18789">
                Купите пакет со скидкой и используйте для любых услуг.
                Бессрочно.
              </p>
              <ul className="space-y-2.5 mb-6" data-id="element-18790">
                <li className="flex items-start text-sm text-slate-600" data-id="element-18791">
                  <CheckIcon className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-18792" />
                  <span data-id="element-18793">
                    <strong data-id="element-18794">20 отчётов</strong> —{' '}
                    {BASE_PRICES[20].toLocaleString('ru-RU')} ₽{' '}
                    <span className="text-slate-400" data-id="element-18795">(3 800 ₽/шт)</span>
                  </span>
                </li>
                <li className="flex items-start text-sm text-slate-600" data-id="element-18796">
                  <CheckIcon className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-18797" />
                  <span data-id="element-18798">
                    <strong data-id="element-18799">50 отчётов</strong> —{' '}
                    {BASE_PRICES[50].toLocaleString('ru-RU')} ₽{' '}
                    <span className="text-slate-400" data-id="element-18800">(3 600 ₽/шт)</span>
                  </span>
                </li>
                <li className="flex items-start text-sm text-slate-600" data-id="element-18801">
                  <CheckIcon className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-18802" />
                  <span data-id="element-18803">
                    <strong data-id="element-18804">100 отчётов</strong> —{' '}
                    {BASE_PRICES[100].toLocaleString('ru-RU')} ₽{' '}
                    <span className="text-slate-400" data-id="element-18805">(3 150 ₽/шт)</span>
                  </span>
                </li>
                <li className="flex items-start text-sm text-slate-600" data-id="element-18806">
                  <CheckIcon className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-18807" />
                  Любые отчёты на выбор
                </li>
                <li className="flex items-start text-sm text-slate-600" data-id="element-18808">
                  <CheckIcon className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-18809" />
                  Бессрочный доступ
                </li>
              </ul>
              <div className="pt-4 border-t border-slate-100 mt-auto" data-id="element-18810">
                <div className="flex items-baseline justify-between mb-4" data-id="element-18811">
                  <span className="text-lg font-bold text-emerald-600" data-id="element-18812">
                    от 3 150 ₽ / отчёт
                  </span>
                </div>
                <Button variant="outline" fullWidth onClick={() => onNavigate('pricing')} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-id="element-18813">
                  Выбрать пакет
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-18814" />
                </Button>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Trust Badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500" data-id="element-18815">
          <span className="flex items-center" data-id="element-18816">
            <ShieldIcon className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-18817" />
            Безопасная оплата
          </span>
          <span className="flex items-center" data-id="element-18818">
            <ClockIcon className="w-4 h-4 mr-1.5 text-indigo-500" data-id="element-18819" />
            Мгновенная доставка
          </span>
          <span className="flex items-center" data-id="element-18820">
            <StarIcon className="w-4 h-4 mr-1.5 text-amber-500" data-id="element-18821" />
            Гарантия 14 дней
          </span>
        </div>
      </div>
    </div>;
}